import { Component, Input, Output, EventEmitter, OnInit, HostListener, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-calendar',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './calendar.html',
    styleUrl: './calendar.scss'
})
export class CalendarComponent implements OnInit {
    @Input() selectedDate: Date | null = null;
    @Output() dateSelect = new EventEmitter<Date>();
    @Output() close = new EventEmitter<void>();

    currentDate: Date = new Date();
    viewDate: Date = new Date();
    localSelectedDate: Date | null = null;

    months: string[] = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    years: number[] = [];

    daysOfWeek: string[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    calendarDays: { date: Date; isCurrentMonth: boolean; isToday: boolean; isSelected: boolean }[] = [];

    ngOnInit() {
        if (this.selectedDate) {
            this.localSelectedDate = new Date(this.selectedDate);
            this.viewDate = new Date(this.selectedDate);
        } else {
            this.localSelectedDate = new Date();
            this.viewDate = new Date();
        }

        const currentYear = new Date().getFullYear();
        for (let i = currentYear - 10; i <= currentYear + 10; i++) {
            this.years.push(i);
        }

        this.generateCalendar();
    }

    generateCalendar() {
        const year = this.viewDate.getFullYear();
        const month = this.viewDate.getMonth();

        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);

        let startDay = firstDayOfMonth.getDay() - 1;
        if (startDay === -1) startDay = 6;

        const days: any[] = [];

        const prevMonthLastDay = new Date(year, month, 0).getDate();
        for (let i = startDay - 1; i >= 0; i--) {
            const date = new Date(year, month - 1, prevMonthLastDay - i);
            days.push({
                date,
                isCurrentMonth: false,
                isToday: this.isToday(date),
                isSelected: this.isSelected(date)
            });
        }

        const lastDate = lastDayOfMonth.getDate();
        for (let i = 1; i <= lastDate; i++) {
            const date = new Date(year, month, i);
            days.push({
                date,
                isCurrentMonth: true,
                isToday: this.isToday(date),
                isSelected: this.isSelected(date)
            });
        }

        const remainingSlots = 42 - days.length;
        for (let i = 1; i <= remainingSlots; i++) {
            const date = new Date(year, month + 1, i);
            days.push({
                date,
                isCurrentMonth: false,
                isToday: this.isToday(date),
                isSelected: this.isSelected(date)
            });
        }

        this.calendarDays = days;
    }

    isToday(date: Date): boolean {
        const today = new Date();
        return date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();
    }

    isSelected(date: Date): boolean {
        if (!this.localSelectedDate) return false;
        return date.getDate() === this.localSelectedDate.getDate() &&
            date.getMonth() === this.localSelectedDate.getMonth() &&
            date.getFullYear() === this.localSelectedDate.getFullYear();
    }

    isMonthDropdownOpen: boolean = false;
    isYearDropdownOpen: boolean = false;

    toggleMonthDropdown(event: Event) {
        event.stopPropagation();
        this.isMonthDropdownOpen = !this.isMonthDropdownOpen;
        this.isYearDropdownOpen = false;
    }

    toggleYearDropdown(event: Event) {
        event.stopPropagation();
        this.isYearDropdownOpen = !this.isYearDropdownOpen;
        this.isMonthDropdownOpen = false;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        this.isMonthDropdownOpen = false;
        this.isYearDropdownOpen = false;
    }

    selectMonth(month: number) {
        this.viewDate = new Date(this.viewDate.getFullYear(), month, 1);
        this.generateCalendar();
        this.isMonthDropdownOpen = false;
    }

    selectYear(year: number) {
        this.viewDate = new Date(year, this.viewDate.getMonth(), 1);
        this.generateCalendar();
        this.isYearDropdownOpen = false;
    }

    onMonthChange(event: any) {
        const month = parseInt(event.target.value);
        this.selectMonth(month);
    }

    onYearChange(event: any) {
        const year = parseInt(event.target.value);
        this.selectYear(year);
    }

    selectDate(day: any) {
        if (!day.isCurrentMonth) return;
        this.localSelectedDate = new Date(day.date);
        this.generateCalendar();
    }

    onSave() {
        if (this.localSelectedDate) {
            this.dateSelect.emit(this.localSelectedDate);
        }
        this.close.emit();
    }
}

